import uniqBy from 'lodash/uniqBy';
import { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { SortOptions } from '../../constants';
import ReviewsApiService from '../../services/api/ReviewsApiService';
import { ErrorLogService } from '../../services/ErrorLogService';
import DateHelper from '../Helpers/DateHelper';
import AdvisorDetails from '../Pages/Advisors/AdvisorDetails/AdvisorDetails';
import LoadingSpinner from '../ui/LoadingAnimations/LoadingSpinner/LoadingSpinner';
import NectarineButton from '../ui/NectarineButton/NectarineButton';
import NectarineSelectInput from '../ui/NectarineSelectInput/NectarineSelectInput';
import { useNotificationsPanelContext } from '../ui/Notifications/NotificationsPanel/NotificationsPanelContext';

interface AdvisorReviewsProps {
  advisorId: string;
  title: string;
  showPrivateComments: boolean;
}

interface ReviewsViewInformation {
  currentPage: number;
  sortOption: SortOptions;
  filteredByRating: number | null;
}

const defaultReviewViewInformation: ReviewsViewInformation = {
  currentPage: 1,
  filteredByRating: null,
  sortOption: SortOptions.Newest
};

const reviewsResultsPerPage = 10;

const AdvisorReviews: React.FC<AdvisorReviewsProps> = (props: AdvisorReviewsProps) => {
  const [isLoadingReviews, setIsLoadingReviews] = useState<boolean>(false);

  const [reviewViewOptions, setReviewViewOptions] = useState<ReviewsViewInformation>(defaultReviewViewInformation);

  const [reviewsAggregateData, setReviewsAggregateData] = useState<Advisor_ReviewsAggregateData_ViewModel | null>(null);

  const [sortedReviewsData, setSortedReviewsData] = useState<Reviews_Review_ViewModel[]>([]);

  const { showErrorNotification } = useNotificationsPanelContext();

  useEffect(() => {
    const loadData = async () => {
      const [reviewsAggregateDataResult, reviewsDataResult] = await Promise.all([
        ReviewsApiService.getAdvisorReviewsAggregateData(props.advisorId),
        ReviewsApiService.getAdvisorReviewsData(
          props.advisorId,
          reviewViewOptions.currentPage,
          reviewViewOptions.sortOption,
          reviewViewOptions.filteredByRating ?? 0,
          props.showPrivateComments
        )
      ]);

      setReviewsAggregateData(reviewsAggregateDataResult);
      setSortedReviewsData(reviewsDataResult);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDisplayedReviews = async (viewOptions: ReviewsViewInformation, changedProperty: keyof ReviewsViewInformation) => {
    try {
      setIsLoadingReviews(true);

      const nextResults = await ReviewsApiService.getAdvisorReviewsData(
        props.advisorId,
        viewOptions.currentPage,
        viewOptions.sortOption,
        viewOptions.filteredByRating ?? 0,
        props.showPrivateComments
      );

      let newReviewsList: Reviews_Review_ViewModel[] = [];

      // Start fresh if the sort or filter options changed
      if (changedProperty === 'sortOption' || changedProperty === 'filteredByRating') {
        newReviewsList = [...nextResults];
      } else {
        newReviewsList = [...sortedReviewsData, ...nextResults];

        // Scroll to the first of the new batch of reviews we just got from the server,
        // if we are appending more reviews to existing ones
        const firstResultOfNewData = nextResults[0];
        if (firstResultOfNewData) {
          setTimeout(() => {
            const elementToScrollTo = document.getElementById(firstResultOfNewData.id);
            if (elementToScrollTo) {
              document.getElementById(firstResultOfNewData.id)?.scrollIntoView();
            }
          }, 500);
        }
      }

      newReviewsList = uniqBy(newReviewsList, (v) => v.id);

      setSortedReviewsData(newReviewsList);
    } catch (error) {
      showErrorNotification('Sorry, but something went wrong when updating the displayed reviews.');

      ErrorLogService.logError(
        `${AdvisorDetails} - updateDisplayedReviews()`,
        `An error occurred while updating displayed reviews.`,
        error
      );
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const getStyleOfReviewBar = (barValue: number): any => {
    if (!reviewsAggregateData) {
      return {};
    }

    const returnValue: any = {
      width: (reviewsAggregateData.reviewsByValue[barValue] / reviewsAggregateData.totalReviews) * 100 + '%'
    };

    return returnValue;
  };

  const getRemainingReviewResultsToDisplay = (): number => {
    if (!reviewsAggregateData) {
      return 0;
    }

    const filteredReviews = reviewsAggregateData?.reviewsByValue[reviewViewOptions.filteredByRating ?? 0];

    return Math.max(0, (filteredReviews ?? reviewsAggregateData.totalReviews) - reviewViewOptions.currentPage * reviewsResultsPerPage);
  };

  const getSortDisplayText = (): string => {
    switch (reviewViewOptions.sortOption) {
      case SortOptions.Highest:
        return 'from highest to lowest';
      case SortOptions.Lowest:
        return 'from lowest to highest';
      case SortOptions.Newest:
        return 'from newest to oldest';
      case SortOptions.Oldest:
        return 'from oldest to newest';
    }
  };

  return (
    <>
      <h1 className="font-rem-2-5">{props.title}</h1>

      {reviewsAggregateData ? (
        <div className="reviewsSummary">
          <div className="d-flex flex-nowrap align-items-center">
            {[...Array(5).keys()].map((currRating) => {
              return Math.floor(reviewsAggregateData.overallRating) > currRating ? (
                <i key={currRating} className={`me-1 fa-solid fa-star greenStar`}></i>
              ) : reviewsAggregateData.overallRating < currRating ? (
                <i key={currRating} className={`me-1 fa-solid fa-star grayStar`}></i>
              ) : (
                // Partial star
                <div key={currRating} className="partialStarArea">
                  <i className={`me-1 fa-solid fa-star grayStar`}></i>

                  <i
                    className={`me-1 fa-solid fa-star greenStar`}
                    style={{ width: (reviewsAggregateData.overallRating - currRating) * 100 + '%' }}
                  ></i>
                </div>
              );
            })}
            <span className="bold ms-3">{reviewsAggregateData.overallRating ? reviewsAggregateData.overallRating.toFixed(1) : 'N/A'}</span>{' '}
            &nbsp;overall rating (<span className="bold">{reviewsAggregateData.totalReviews}</span> &nbsp;review
            {reviewsAggregateData.totalReviews === 1 ? '' : 's'})
          </div>

          <div className="ratingBarsArea mt-4">
            {[...Array(5).keys()].reverse().map((currRating) => {
              return (
                <div
                  key={currRating}
                  className={`ratingBar pointer ${reviewViewOptions.filteredByRating === currRating + 1 ? 'isSelected' : ''}`}
                  onClick={() => {
                    const newVal: ReviewsViewInformation = {
                      ...reviewViewOptions,
                      filteredByRating: reviewViewOptions.filteredByRating === currRating + 1 ? null : currRating + 1,
                      currentPage: 1
                    };

                    setReviewViewOptions(newVal);
                    updateDisplayedReviews(newVal, 'filteredByRating');
                  }}
                  title={`${reviewsAggregateData.reviewsByValue[currRating + 1]}`}
                >
                  <span className="ratingLabel">{currRating + 1}</span>
                  <span
                    className={`filledInArea ${reviewViewOptions.filteredByRating === currRating + 1 ? 'isSelected' : ''}`}
                    style={{ ...getStyleOfReviewBar(currRating + 1) }}
                  ></span>
                </div>
              );
            })}
          </div>
          <div className="mt-5">
            <Label for="reviewsSort" className="bold">
              Sort
            </Label>
          </div>

          <div className="w-25">
            <NectarineSelectInput
              style="rounded"
              items={[
                { name: 'Newest First', value: SortOptions.Newest },
                { name: 'Oldest First', value: SortOptions.Oldest },
                { name: 'Highest First', value: SortOptions.Highest },
                { name: 'Lowest First', value: SortOptions.Lowest }
              ]}
              value={reviewViewOptions.sortOption}
              onChange={(v) => {
                const newVal: ReviewsViewInformation = {
                  ...reviewViewOptions,
                  sortOption: v as number,
                  currentPage: 1
                };

                // If the user sorts by highest or lowest, clear the filter
                if ([SortOptions.Highest, SortOptions.Lowest].includes(newVal.sortOption)) {
                  newVal.filteredByRating = null;
                }

                setReviewViewOptions(newVal);
                updateDisplayedReviews(newVal, 'sortOption');
              }}
            />
          </div>

          {!sortedReviewsData.length ? (
            <div className="mt-5 mb-3 text-center">
              {reviewsAggregateData.totalReviews > 0 ? (
                <Label>
                  No reviews were found that matched your filters.
                  <br />
                  <span
                    className="underline pointer"
                    onClick={() => {
                      const newObj: ReviewsViewInformation = {
                        ...defaultReviewViewInformation,
                        sortOption: reviewViewOptions.sortOption
                      };

                      setReviewViewOptions(newObj);
                      updateDisplayedReviews(newObj, 'filteredByRating');
                    }}
                  >
                    Show all reviews.
                  </span>
                </Label>
              ) : (
                <Label>No reviews were found.</Label>
              )}
            </div>
          ) : (
            <></>
          )}

          {sortedReviewsData.length && reviewViewOptions.filteredByRating ? (
            <div className="mt-3">
              Showing <span className="bold">{reviewViewOptions.filteredByRating} star reviews</span>
              &nbsp;{getSortDisplayText()}. &nbsp;
              <Label>
                <span
                  className="underline pointer"
                  onClick={() => {
                    const newObj: ReviewsViewInformation = {
                      ...defaultReviewViewInformation,
                      sortOption: reviewViewOptions.sortOption
                    };

                    setReviewViewOptions(newObj);
                    updateDisplayedReviews(newObj, 'filteredByRating');
                  }}
                >
                  Show all reviews.
                </span>
              </Label>
            </div>
          ) : (
            <></>
          )}

          <div className="mt-5"></div>

          {sortedReviewsData.map((currReview) => (
            <div key={currReview.id} id={currReview.id} className="details-review">
              {[...Array(5).keys()].map((v) => {
                return <i key={v} className={`me-1 fa-solid fa-star ${v < Math.floor(currReview.rating) ? 'greenStar' : 'grayStar'}`}></i>;
              })}

              <h3>{currReview.title}</h3>
              <p>{currReview.body}</p>

              {currReview.privateComments ? (
                <div className="my-2">
                  <strong>Private Notes From {currReview.displayName}</strong>
                  <div>{currReview.privateComments}</div>
                </div>
              ) : (
                <></>
              )}

              <div className="review-footer">
                <div className="reviewer-name">{currReview.displayName}</div>
                <div className="review-date">{DateHelper.printMonthYearLong(currReview.modifiedAt)}</div>
              </div>
            </div>
          ))}

          {isLoadingReviews ? <LoadingSpinner message="Loading reviews..." /> : <></>}

          {getRemainingReviewResultsToDisplay() ? (
            <div className="text-center mb-5">
              <NectarineButton
                text={`Show more (${getRemainingReviewResultsToDisplay()})`}
                color="white"
                onClick={() => {
                  const newVal: ReviewsViewInformation = {
                    ...reviewViewOptions,
                    currentPage: reviewViewOptions.currentPage + 1
                  };

                  setReviewViewOptions(newVal);
                  updateDisplayedReviews(newVal, 'currentPage');
                }}
              />
            </div>
          ) : (
            <></>
          )}

          {sortedReviewsData.length && reviewViewOptions.filteredByRating !== null && !getRemainingReviewResultsToDisplay() ? (
            <div className="text-center mb-5">
              <NectarineButton
                text="Show all reviews"
                color="white"
                onClick={() => {
                  const newVal: ReviewsViewInformation = {
                    ...reviewViewOptions,
                    currentPage: 1,
                    filteredByRating: null
                  };

                  setReviewViewOptions(newVal);
                  updateDisplayedReviews(newVal, 'filteredByRating');
                }}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default AdvisorReviews;
